/**
 * Ciclo de vida del `MediaRecorder`.
 *
 * Lo que el mockup no resuelve y acá sí: el estado `requesting` (entre el click y el prompt del
 * navegador pasan segundos y el botón parece muerto), el permiso denegado, el tope de duración, y
 * sobre todo el **cleanup de los tracks**.
 *
 * Nació en el widget de feedback (`feedback_widget/useAudioRecorder.js`, hoy un re-export de esto)
 * y se compartió cuando la Barra Total de la Memoria sumó la nota de audio. Los defaults son los
 * del widget; la Memoria pasa los suyos (`maxSeconds`, `maxBytes`, `errorKeyPrefix`…).
 *
 * `onReady(audio, {reason})` dice POR QUÉ se detuvo: `'user'` (apretó parar), `'hidden'` (cambió
 * de pestaña), `'limit'` (tope de tiempo o de bytes) o `'pagehide'`. Le importa a quien manda solo
 * al parar: cambiar de pestaña a mitad de una nota para buscar un dato es normal, y mandarla sin
 * preguntar sería el peor resultado.
 */
import {useCallback, useEffect, useRef, useState} from 'react';

import {
    DEFAULT_ERROR_KEY_PREFIX,
    MAX_AUDIO_BYTES,
    MAX_AUDIO_SECONDS,
    METER_BANDS,
    NEAR_LIMIT_SECONDS,
    SILENCE_PEAK_LEVEL,
    bandsFromFrequencies,
    describeMicError,
    extensionForMimeType,
    isAudioRecordingSupported,
    levelFromTimeDomain,
    pickAudioMimeType,
} from '../utils/audioRecording';

export const STATUS_IDLE = 'idle';
export const STATUS_REQUESTING = 'requesting';
export const STATUS_RECORDING = 'recording';
export const STATUS_READY = 'ready';
export const STATUS_ERROR = 'error';

export const STOP_REASON_USER = 'user';
export const STOP_REASON_HIDDEN = 'hidden';
export const STOP_REASON_LIMIT = 'limit';
export const STOP_REASON_PAGEHIDE = 'pagehide';

export function useAudioRecorder({
    maxSeconds = MAX_AUDIO_SECONDS,
    maxBytes = MAX_AUDIO_BYTES,
    nearLimitSeconds = NEAR_LIMIT_SECONDS,
    errorKeyPrefix = DEFAULT_ERROR_KEY_PREFIX,
    filenameBase = 'feedback-audio',
    id = 'fb-audio',
    // Best-effort: Chrome y Firefox lo honran, Safari puede ignorarlo.
    bitsPerSecond,
    // Un `objectURL` para reproducir la grabación. Opt-in porque su PROPIEDAD pasa al consumidor y
    // este hook nunca lo revoca (revocarlo en su unmount rompería el player al reabrir el panel):
    // quien lo pide se hace cargo. El widget de feedback tiene player y lo necesita; la Memoria
    // manda el audio y no lo reproduce nunca, así que pedirlo ahí dejaba cada nota —hasta 19 MB—
    // retenida en memoria hasta recargar la página.
    withPreviewUrl = true,
    // Mide el nivel de entrada en vivo (`AnalyserNode`) para dibujar un medidor y para saber si en
    // toda la grabación hubo sonido. Opt-in: monta un `AudioContext` por grabación, y quien no
    // dibuja nada no tiene por qué pagarlo.
    withMeter = false,
    onReady,
} = {}) {
    const [status, setStatus] = useState(STATUS_IDLE);
    const [elapsedMs, setElapsedMs] = useState(0);
    const [errorKey, setErrorKey] = useState(null);

    const recorderRef = useRef(null);
    const streamRef = useRef(null);
    const chunksRef = useRef([]);
    const startedAtRef = useRef(0);
    const tickRef = useRef(null);
    const bytesRef = useRef(0);
    const stopReasonRef = useRef(STOP_REASON_USER);
    const discardRef = useRef(false);
    // `onReady` cambia de identidad en cada render del consumidor; el `onstop` del recorder se
    // arma una vez por grabación y tiene que llamar a la versión vigente.
    const onReadyRef = useRef(onReady);
    onReadyRef.current = onReady;

    // --- medición de nivel --- //
    // El medidor se lee por REF, no por estado: con `setState` a 60 fps el consumidor entero se
    // re-renderiza sesenta veces por segundo mientras alguien habla. Acá se escribe el objeto y el
    // componente que dibuja lo lee en su propio `requestAnimationFrame`.
    const meterRef = useRef({level: 0, bands: new Array(METER_BANDS).fill(0), active: false});
    const audioCtxRef = useRef(null);
    const analyserRef = useRef(null);
    const rafRef = useRef(null);
    // `null` (y no `0`) mientras no se midió NADA: sin medición no se puede afirmar que hubo
    // silencio, y avisar "no se detectó sonido" sin haber escuchado sería mentir.
    const peakRef = useRef(null);

    const stopMetering = useCallback(() => {
        if (rafRef.current && typeof cancelAnimationFrame === 'function') {
            cancelAnimationFrame(rafRef.current);
        }
        rafRef.current = null;
        analyserRef.current = null;
        const ctx = audioCtxRef.current;
        audioCtxRef.current = null;
        if (ctx && typeof ctx.close === 'function' && ctx.state !== 'closed') {
            // El `AudioContext` es un recurso del sistema: no cerrarlo deja el pipeline de audio
            // vivo y el navegador limita cuántos puede haber abiertos a la vez.
            try {
                const closing = ctx.close();
                if (closing && typeof closing.catch === 'function') closing.catch(() => {});
            } catch (_) {
                /* no-op */
            }
        }
        meterRef.current = {level: 0, bands: new Array(METER_BANDS).fill(0), active: false};
    }, []);

    /**
     * Engancha un `AnalyserNode` al stream y actualiza `meterRef` frame a frame.
     *
     * BEST-EFFORT en serio: si Web Audio no existe o falla, se graba igual y el medidor queda
     * quieto — perder el adorno no puede costar la nota.
     */
    const startMetering = useCallback((stream) => {
        if (!withMeter || typeof window === 'undefined') return;
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx || typeof requestAnimationFrame !== 'function') return;
        try {
            const ctx = new Ctx();
            // Chrome puede entregarlo suspendido; el click que abrió el micrófono ya es el gesto
            // que lo habilita, así que alcanza con pedirlo.
            if (ctx.state === 'suspended' && typeof ctx.resume === 'function') {
                const resuming = ctx.resume();
                if (resuming && typeof resuming.catch === 'function') resuming.catch(() => {});
            }
            const analyser = ctx.createAnalyser();
            // 1024 muestras ≈ 21 ms a 48 kHz: suficiente resolución para separar bandas de voz sin
            // que el medidor se vuelva lento de leer.
            analyser.fftSize = 1024;
            // Suaviza el espectro entre frames: sin esto las barras tiemblan y marean.
            analyser.smoothingTimeConstant = 0.75;
            ctx.createMediaStreamSource(stream).connect(analyser);
            audioCtxRef.current = ctx;
            analyserRef.current = analyser;
            peakRef.current = 0;

            const timeData = new Uint8Array(analyser.fftSize);
            const freqData = new Uint8Array(analyser.frequencyBinCount);
            const tick = () => {
                const node = analyserRef.current;
                if (!node) return;
                node.getByteTimeDomainData(timeData);
                node.getByteFrequencyData(freqData);
                const level = levelFromTimeDomain(timeData);
                peakRef.current = Math.max(peakRef.current || 0, level);
                meterRef.current = {
                    level,
                    bands: bandsFromFrequencies(freqData, METER_BANDS),
                    active: true,
                };
                rafRef.current = requestAnimationFrame(tick);
            };
            rafRef.current = requestAnimationFrame(tick);
        } catch (_) {
            stopMetering();
        }
    }, [stopMetering, withMeter]);

    /**
     * Corta el stream del micrófono. Es LO MÁS IMPORTANTE de este hook: sin esto el indicador rojo
     * de "grabando" queda pegado en la pestaña del navegador para siempre y el usuario cree que lo
     * seguimos escuchando. Es un bug de confianza, no de memoria.
     */
    const stopTracks = useCallback(() => {
        // El medidor cuelga del MISMO stream: se apaga acá para que no exista un solo camino que
        // libere el micrófono y deje el `AudioContext` abierto.
        stopMetering();
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => {
                try {
                    track.stop();
                } catch (_) {
                    /* no-op */
                }
            });
            streamRef.current = null;
        }
    }, [stopMetering]);

    const clearTick = useCallback(() => {
        if (tickRef.current) {
            clearInterval(tickRef.current);
            tickRef.current = null;
        }
    }, []);

    const stopWith = useCallback((reason) => {
        const recorder = recorderRef.current;
        if (recorder && recorder.state === 'recording') {
            stopReasonRef.current = reason;
            try {
                recorder.stop();
            } catch (_) {
                stopTracks();
            }
        }
    }, [stopTracks]);

    const stop = useCallback(() => stopWith(STOP_REASON_USER), [stopWith]);

    /** Detiene y DESCARTA lo grabado: sin `onReady`, sin blob. Vuelve a idle. */
    const cancel = useCallback(() => {
        const recorder = recorderRef.current;
        if (recorder && recorder.state === 'recording') {
            discardRef.current = true;
            stopWith(STOP_REASON_USER);
            return;
        }
        clearTick();
        stopTracks();
        setStatus(STATUS_IDLE);
        setElapsedMs(0);
    }, [clearTick, stopTracks, stopWith]);

    const start = useCallback(async () => {
        if (!isAudioRecordingSupported()) {
            setStatus(STATUS_ERROR);
            setErrorKey(`${errorKeyPrefix}.error_generic`);
            return;
        }
        setErrorKey(null);
        discardRef.current = false;
        // Sin este estado, entre el click y el "Permitir" del navegador el botón parece colgado.
        setStatus(STATUS_REQUESTING);

        let stream;
        try {
            stream = await navigator.mediaDevices.getUserMedia({audio: true});
        } catch (error) {
            setStatus(STATUS_ERROR);
            setErrorKey(describeMicError(error, errorKeyPrefix));
            return;
        }

        streamRef.current = stream;
        chunksRef.current = [];
        bytesRef.current = 0;
        peakRef.current = null;
        startMetering(stream);

        const {mimeType} = pickAudioMimeType();
        let recorder;
        try {
            const options = {};
            if (mimeType) options.mimeType = mimeType;
            if (bitsPerSecond) options.audioBitsPerSecond = bitsPerSecond;
            recorder = Object.keys(options).length
                ? new MediaRecorder(stream, options)
                : new MediaRecorder(stream);
        } catch (_) {
            stopTracks();
            setStatus(STATUS_ERROR);
            setErrorKey(`${errorKeyPrefix}.error_generic`);
            return;
        }
        recorderRef.current = recorder;
        stopReasonRef.current = STOP_REASON_USER;

        recorder.ondataavailable = (event) => {
            if (event.data && event.data.size) {
                chunksRef.current.push(event.data);
                bytesRef.current += event.data.size;
                if (bytesRef.current > maxBytes) stopWith(STOP_REASON_LIMIT);
            }
        };

        recorder.onstop = () => {
            clearTick();
            stopTracks();
            if (discardRef.current) {
                discardRef.current = false;
                chunksRef.current = [];
                setStatus(STATUS_IDLE);
                setElapsedMs(0);
                return;
            }
            // El mimeType REAL puede diferir del pedido (el browser elige cuando pasamos '').
            const realMimeType = recorder.mimeType || mimeType || 'audio/webm';
            const blob = new Blob(chunksRef.current, {type: realMimeType});
            chunksRef.current = [];
            const durationSeconds = Math.round((Date.now() - startedAtRef.current) / 1000);
            // `stopTracks()` ya corrió acá arriba y apagó el medidor: el pico sobrevive porque
            // `stopMetering` resetea lo que se DIBUJA (`meterRef`), no lo que se midió.
            const peakLevel = peakRef.current;
            const audio = {
                id,
                blob,
                filename: `${filenameBase}.${extensionForMimeType(realMimeType)}`,
                bytes: blob.size,
                durationSeconds,
                previewUrl: withPreviewUrl ? URL.createObjectURL(blob) : null,
                // `null` = no se midió (sin medidor, o Web Audio no disponible). Sólo `false`
                // habilita a decirle al usuario que no se detectó sonido.
                hadSound: peakLevel === null ? null : peakLevel >= SILENCE_PEAK_LEVEL,
                peakLevel,
            };
            setStatus(STATUS_READY);
            // La PROPIEDAD del objectURL pasa al consumidor. Este hook nunca revoca en su unmount:
            // si lo hiciera, cerrar el panel rompería el player al reabrirlo.
            if (onReadyRef.current) onReadyRef.current(audio, {reason: stopReasonRef.current});
        };

        startedAtRef.current = Date.now();
        setElapsedMs(0);
        recorder.start();
        setStatus(STATUS_RECORDING);

        // El valor sale del reloj, no de un contador incremental: un `setInterval` en una pestaña
        // de fondo se throttlea a 1/min y el contador mentiría descaradamente.
        tickRef.current = setInterval(() => {
            const elapsed = Date.now() - startedAtRef.current;
            setElapsedMs(elapsed);
            if (elapsed >= maxSeconds * 1000) stopWith(STOP_REASON_LIMIT);
        }, 250);
    }, [bitsPerSecond, clearTick, errorKeyPrefix, filenameBase, id, maxBytes, maxSeconds, stopTracks,
        stopWith, withPreviewUrl]);

    const reset = useCallback(() => {
        setStatus(STATUS_IDLE);
        setElapsedMs(0);
        setErrorKey(null);
    }, []);

    // Cleanup en unmount: si el panel se cierra grabando, el micrófono se libera igual.
    useEffect(() => () => {
        clearTick();
        const recorder = recorderRef.current;
        if (recorder && recorder.state === 'recording') {
            try {
                recorder.stop();
            } catch (_) {
                /* no-op */
            }
        }
        stopTracks();
    }, [clearTick, stopTracks]);

    // Un `MediaRecorder` vivo en una pestaña de fondo mantiene el micrófono caliente. Se para y se
    // conserva lo grabado, en vez de descartarlo — y se dice por qué se paró.
    useEffect(() => {
        const onHidden = () => {
            if (document.visibilityState === 'hidden') stopWith(STOP_REASON_HIDDEN);
        };
        const onPageHide = () => {
            stopWith(STOP_REASON_PAGEHIDE);
            stopTracks();
        };
        document.addEventListener('visibilitychange', onHidden);
        window.addEventListener('pagehide', onPageHide);
        return () => {
            document.removeEventListener('visibilitychange', onHidden);
            window.removeEventListener('pagehide', onPageHide);
        };
    }, [stopTracks, stopWith]);

    const remainingSeconds = Math.max(0, maxSeconds - Math.floor(elapsedMs / 1000));

    return {
        supported: isAudioRecordingSupported(),
        // Ref viva con `{level, bands, active}`. Se lee en un rAF propio: leerla en el render no
        // sirve (React no re-renderiza porque una ref cambie) y ese es justamente el punto.
        meterRef,
        status,
        elapsedMs,
        errorKey,
        isNearLimit: status === STATUS_RECORDING && remainingSeconds <= nearLimitSeconds,
        remainingSeconds,
        start,
        stop,
        cancel,
        reset,
    };
}

export default useAudioRecorder;
